import React, { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { isNativeIOS, isNativeAndroid } from '@/lib/capacitor';
import { purchaseProWithAppleIAP } from '@/lib/iap';
import { isDesktopApp, isMacAppStore } from '@/lib/desktopBridge';
import { purchaseProWithMacIAP, restoreProWithMacIAP } from '@/lib/macIap';

export type SubscriptionTier = 'basic' | 'pro' | 'free_for_life';
export type SubscriptionStatus = 'active' | 'trialing' | 'expired' | 'canceled';

interface Subscription {
  id: string;
  user_id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  apple_transaction_id: string | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
}

interface SubscriptionFeatures {
  unlimitedHistory: boolean;
  advancedInsights: boolean;
  unlimitedTags: boolean;
  cloudSync: boolean;
  weeklySummaries: boolean;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  isProUser: boolean;
  trialDaysRemaining: number | null;
  features: SubscriptionFeatures;
  loading: boolean;
  redeemCoupon: (code: string) => Promise<{ success: boolean; error?: string; tier?: SubscriptionTier }>;
  startTrial: () => Promise<{ success: boolean; error?: string }>;
  upgradeToPro: () => Promise<{ success: boolean; checkoutUrl?: string; error?: string }>;
  manageSubscription: () => Promise<{ success: boolean; portalUrl?: string; error?: string }>;
  /** Restore a previous Mac App Store purchase (no-op elsewhere). */
  restorePurchases: () => Promise<{ success: boolean; error?: string }>;
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

function getFeatures(tier: SubscriptionTier, status: SubscriptionStatus): SubscriptionFeatures {
  const isPro = (tier === 'pro' || tier === 'free_for_life') && (status === 'active' || status === 'trialing');
  
  return {
    unlimitedHistory: isPro,
    advancedInsights: isPro,
    unlimitedTags: isPro,
    cloudSync: isPro,
    weeklySummaries: isPro,
  };
}

function calculateTrialDaysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  
  const now = new Date();
  const trialEnd = new Date(trialEndsAt);
  const diffMs = trialEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [hasProGrant, setHasProGrant] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setHasProGrant(false);
      setLoading(false);
      return;
    }

    try {
      const [subResult, adminResult] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }),
      ]);

      if (subResult.error) {
        console.error('Error fetching subscription:', subResult.error);
      }
      setSubscription(subResult.data as Subscription | null);
      // Pro access: user has admin role
      const isAdmin = adminResult.data === true;
      setHasProGrant(isAdmin);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Desktop: Stripe checkout opens in the external browser, so re-check the
  // subscription when the app window regains focus to unlock Pro on return.
  useEffect(() => {
    if (!isDesktopApp()) return;
    const onFocus = () => fetchSubscription();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchSubscription]);

  const redeemCoupon = async (code: string): Promise<{ success: boolean; error?: string; tier?: SubscriptionTier }> => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { data, error } = await supabase.functions.invoke('redeem-coupon', {
        body: { code: code.toUpperCase().trim() }
      });

      if (error) throw error;
      
      if (data.success) {
        await fetchSubscription();
        return { success: true, tier: data.tier };
      } else {
        return { success: false, error: data.error || 'Invalid coupon code' };
      }
    } catch (err: unknown) {
      console.error('Error redeeming coupon:', err);
      const message = err instanceof Error ? err.message : 'Failed to redeem coupon';
      return { success: false, error: message };
    }
  };

  const startTrial = async (): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 7);

      const { error } = await supabase
        .from('subscriptions')
        .upsert({
          user_id: user.id,
          tier: 'pro',
          status: 'trialing',
          trial_ends_at: trialEndsAt.toISOString(),
        }, { onConflict: 'user_id' });

      if (error) throw error;

      await fetchSubscription();
      return { success: true };
    } catch (err: unknown) {
      console.error('Error starting trial:', err);
      const message = err instanceof Error ? err.message : 'Failed to start trial';
      return { success: false, error: message };
    }
  };

  const upgradeToPro = async (): Promise<{ success: boolean; checkoutUrl?: string; error?: string }> => {
    if (!user) return { success: false, error: 'Not authenticated' };

    // Native iOS app (App Store): Apple In-App Purchase
    if (isNativeIOS()) {
      try {
        const result = await purchaseProWithAppleIAP();
        if (result.success) await fetchSubscription();
        return result;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to complete purchase';
        return { success: false, error: message };
      }
    }

    // Mac App Store build: Apple In-App Purchase (StoreKit). Stripe is not
    // permitted inside a Mac App Store app.
    if (isMacAppStore()) {
      try {
        const result = await purchaseProWithMacIAP();
        if (result.success) await fetchSubscription();
        return result;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to complete purchase';
        return { success: false, error: message };
      }
    }

    // Native Android app (Play Store): in-app billing (Stripe not used in native app)
    if (isNativeAndroid()) {
      return {
        success: false,
        error: 'Subscriptions are managed in the app. Use the in-app purchase option when available, or open Luma in a browser to subscribe with a card.',
      };
    }

    // Desktop or mobile browser: Stripe Checkout (shouldUseStripeCheckout() is true here)
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { userId: user.id },
      });

      if (error) {
        const msg = error?.message ?? '';
        if (msg.includes('non-2xx') || msg.includes('FunctionsHttp')) {
          return { success: false, error: 'Checkout is temporarily unavailable. Please try again in a moment or contact support.' };
        }
        throw error;
      }

      if (data?.url) {
        return { success: true, checkoutUrl: data.url };
      }
      return { success: false, error: data?.error ?? 'Failed to create checkout session' };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upgrade';
      return { success: false, error: message };
    }
  };

  const manageSubscription = async (): Promise<{ success: boolean; portalUrl?: string; error?: string }> => {
    if (!user) return { success: false, error: 'Not authenticated' };

    if (isNativeIOS()) {
      return {
        success: false,
        error: 'On iOS, manage your subscription in Settings → Apple ID → Subscriptions.',
      };
    }

    if (isMacAppStore()) {
      return {
        success: false,
        error: 'Manage your subscription in the App Store → your account → Subscriptions.',
      };
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-customer-portal');

      if (error) throw error;

      if (data.url) {
        return { success: true, portalUrl: data.url };
      } else {
        return { success: false, error: data.error || 'Failed to create portal session' };
      }
    } catch (err: unknown) {
      console.error('Error opening customer portal:', err);
      const message = err instanceof Error ? err.message : 'Failed to open portal';
      return { success: false, error: message };
    }
  };

  const restorePurchases = async (): Promise<{ success: boolean; error?: string }> => {
    if (!isMacAppStore()) {
      return { success: false, error: 'Restore is only available in the Mac App Store app.' };
    }
    try {
      const result = await restoreProWithMacIAP();
      if (result.success) await fetchSubscription();
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to restore purchases';
      return { success: false, error: message };
    }
  };

  // Admin-granted Pro overrides subscription for feature access
  const tier = hasProGrant ? 'pro' : (subscription?.tier || 'basic');
  const status = hasProGrant ? 'active' : (subscription?.status || 'active');
  const isProUser = (tier === 'pro' || tier === 'free_for_life') && (status === 'active' || status === 'trialing');
  const trialDaysRemaining = calculateTrialDaysRemaining(subscription?.trial_ends_at || null);
  const features = getFeatures(tier, status);

  return (
    <SubscriptionContext.Provider 
      value={{ 
        subscription, 
        tier, 
        status, 
        isProUser, 
        trialDaysRemaining,
        features,
        loading, 
        redeemCoupon, 
        startTrial,
        upgradeToPro,
        manageSubscription,
        restorePurchases,
        refetch: fetchSubscription
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
